<template>
  <InteractiveModal
    modalId="strategyModal"
    :modalTitle="isNew ? 'Create Strategy' : 'Edit Strategy'"
    :modalContent="displayModalContent"
    :isModalActive="isModalActive"
    :isMinimized="isMinimized"
    :draggable="draggable"
    :isDirty="isDirty"
    @close="$emit('close')"
    @minimize="$emit('minimize')"
    @restore="$emit('restore')"
  >
    <!-- Header is now part of InteractiveModal, title passed as prop -->
    <template slot="modal-body">
      <b-field
        label="Title"
        :message="errorMessage.title"
        :type="errorMessage.title ? 'is-danger' : undefined"
      >
        <b-input
          v-model="dirtyItem.title"
          type="textarea"
          @input="validateData()"
        />
      </b-field>
      <b-field label="Domain/Category">
        <DomainCategoryDropdown
          v-if="!isDomainCategoriesLoading"
          modalTitle="Strategy"
          :isNew="isNew"
          :projectId="projectId"
          :gripDomainCategories="gripDomainCategoryOptions"
          :gripDomainCategoryDetails="gripDomainCategoryDetails"
          @selectedGripDomainCategory="handleSelectedGripDomainCategory"
          @close="$emit('close')"
        />
      </b-field>
      <div>
        <b-field
          label="Description"
          :message="errorMessage.description"
          :type="errorMessage.description ? 'is-danger' : undefined"
          class="width-full display-block-important"
        >
          <b-input
            v-model="dirtyItem.description"
            type="textarea"
            @input="validateData()"
          />
          <span
            :class="[$style.descriptionWordCount, 'align-end', 'display-flex']"
            >{{ wordCount(dirtyItem.description) }}/150 words</span
          >
        </b-field>
      </div>
      <b-field label="Link(s)" class="width-full">
        <div>
          <div v-for="(link, linkIndex) in itemLinks" :key="linkIndex">
            <div :class="$style.linkContainer">
              <b-field
                class="width-100 no-bottom-margin"
                :message="link.errorMessage"
                :type="!link.isValid ? 'is-danger' : undefined"
              >
                <b-input
                  v-model="link.url"
                  :class="[
                    $style.linkInput,
                    'width-100',
                    'bg-white',
                    'align-right',
                  ]"
                  type="text"
                  @input="validateGripLink(linkIndex)"
                >
                </b-input>
              </b-field>
              <a
                :class="[
                  $style.deleteLinkColor,
                  'padding-top12',
                  'margin-left16',
                  'cursor-pointer',
                ]"
                @click="removeLinkTextInput(linkIndex)"
              >
                <b-icon icon="delete" size="is-small"></b-icon>
              </a>
            </div>
            <div v-if="link.url && link.isValid">
              <a target="_blank" :class="$style.sourceLink" :href="sourceLink">
                Go to source link
                <b-icon icon="launch" size="is-small"></b-icon>
              </a>
            </div>
          </div>
          <a
            class="add-participant-text cursor-pointer"
            @click="addLinkTextInput"
          >
            + Add Link
          </a>
        </div>
      </b-field>
      <b-field label="Attachment(s)">
        <GenericFileUploader :fileAttachment="fileAttachment" />
      </b-field>
      <b-field label="Resources">
        <b-input v-model="dirtyItem.resource" type="textarea" />
      </b-field>
      <b-field label="How the Strategy will be measured or evaluated">
        <b-input v-model="dirtyItem.method" type="textarea" />
      </b-field>
    </template>
    <!-- Footer is now part of InteractiveModal -->
    <template slot="modal-footer">
      <button
        v-if="
          gripStrategyDetails && Object.keys(gripStrategyDetails).length > 0
        "
        class="button is-primary is-rounded"
        @click="onUpdateGripStrategy()"
      >
        Save
      </button>
      <button
        v-else
        class="button is-primary is-rounded"
        @click="onAddGripStrategy()"
      >
        Create Strategy
      </button>
    </template>
  </InteractiveModal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { Action, State } from 'vuex-class';
import GenericFileUploader from '../../../components/uploader/GenericFileUploader.vue';
import { isTruthy } from '../../../jbi-shared/util/watcher.vue-decorator';
import { ToastProgrammatic as Toast } from 'buefy';
import {
  CreateGripStrategyPayload,
  FileAttachmentDTO,
  GripStrategyDTO,
  GripDomainCategory,
  LinkDTO,
  UpdateGripStrategyPayload,
  CreateGripStrategyBase,
} from '../../../store/types/grip.types';
import { ApiState } from '../../../store/types/general.types';
import DomainCategoryDropdown from '../DomainCategory/DomainCategoryDropdown.vue';
import {
  getSourceLink,
  getWordCount,
  validateLink,
} from '../../../utils/general.util';
import InteractiveModal from '@/components/base/InteractiveModal.vue';
import GripModalMixin from '@/views/Grip/components/grip-modal.mixin';

@Component({
  components: { DomainCategoryDropdown, GenericFileUploader, InteractiveModal },
})
export default class StrategyModal extends mixins(GripModalMixin) {
  @Prop() public gripStrategyDetails!: GripStrategyDTO;

  get gripDomainCategoryDetails(): GripDomainCategory | null {
    return this.gripStrategyDetails?.gripDomainCategory ?? null;
  }

  public dirtyItem: CreateGripStrategyBase = {
    title: '',
    description: '',
    resource: '',
    method: '',
    links: [],
    attachments: [],
    gripDomainCategoryId: null,
  };

  public itemLinks: LinkDTO[] = [];

  public errorMessage: {
    title: string | null;
    description: string | null;
  } = { title: null, description: null };

  public validateData() {
    if (!this.dirtyItem.title.trim().length) {
      this.errorMessage.title = 'This Field is Required';
    } else {
      this.errorMessage.title = null;
    }

    if (
      this.dirtyItem.description &&
      this.wordCount(this.dirtyItem.description.trim()) > 150
    ) {
      this.errorMessage.description = 'Max 150 words are allowed.';
    } else {
      this.errorMessage.description = null;
    }

    return this.errorMessage;
  }

  public onAddGripStrategy(): void {
    this.validateData();
    if (
      !this.errorMessage.title &&
      !this.errorMessage.description &&
      this.areLinksValid
    ) {
      this.getGripItemLinks();
      this.getItemAttachments();
      this.dirtyItem.gripDomainCategoryId = this.selectedGripDomainCategoryId;
      this.$emit('create', this.dirtyItem);
    }
  }

  public onUpdateGripStrategy(): void {
    this.getGripItemLinks();
    this.getItemAttachments();
    this.dirtyItem.gripDomainCategoryId = this.selectedGripDomainCategoryId;
    const strategy: { [key: string]: any } = { ...this.dirtyItem };
    const strategyInfo: { [key: string]: any } = {
      ...this.gripStrategyDetails,
    };
    let updateStrategy: Partial<UpdateGripStrategyPayload> = {};
    Object.keys(strategy).forEach((key: string) => {
      if (key === 'gripDomainCategoryId') {
        if (strategyInfo.gripDomainCategory) {
          if (
            JSON.stringify(strategy.gripDomainCategoryId) !==
            JSON.stringify(strategyInfo.gripDomainCategory.id)
          ) {
            updateStrategy.gripDomainCategoryId = strategy.gripDomainCategoryId;
          }
        } else if (
          strategyInfo.gripDomainCategory !== strategy.gripDomainCategoryId
        ) {
          updateStrategy.gripDomainCategoryId = strategy.gripDomainCategoryId;
        }
      } else {
        if (
          strategyInfo.hasOwnProperty(key) &&
          JSON.stringify(strategy[key]) !== JSON.stringify(strategyInfo[key])
        ) {
          (updateStrategy as any)[key] = strategy[key];
        } else if (
          !strategyInfo.hasOwnProperty(key) &&
          strategy[key] !== undefined &&
          strategy[key] !== null &&
          (Array.isArray(strategy[key])
            ? strategy[key].length > 0
            : strategy[key] !== '')
        ) {
          // If key is not in original details but has a value in the form, consider it a change
          // (e.g. adding links to an item that previously had none)
          (updateStrategy as any)[key] = strategy[key];
        }
      }
    });
    this.validateData(); // Validate before emitting
    if (
      !this.errorMessage.title &&
      !this.errorMessage.description &&
      this.areLinksValid
    ) {
      // Emit 'update' with the ID and the changes.
      // If Object.keys(updateStrategy).length is 0, parent can decide not to make API call.
      this.$emit('update', {
        id: this.gripStrategyDetails.id,
        ...updateStrategy,
      });
    }
  }

  public mounted(): void {
    this.isDomainCategoriesLoading = true;
    this.getGripDomainCategories(this.projectId);
    if (
      this.gripStrategyDetails &&
      Object.keys(this.gripStrategyDetails).length > 0
    ) {
      const {
        gripDomainCategory,
        id,
        ...strategyInfo
      } = this.gripStrategyDetails;
      strategyInfo.links.forEach((link: string) => {
        this.count++;
        this.itemLinks.push({
          url: link,
          count: this.count,
          isValid: true,
          errorMessage: '',
        });
      });

      strategyInfo.attachments.forEach((file) => {
        const fileData = file.split('/');
        this.fileAttachment.push({
          type: file,
          name: fileData[fileData.length - 1],
        });
      });

      this.selectedGripDomainCategoryId = gripDomainCategory
        ? gripDomainCategory.id
        : null;

      this.dirtyItem = {
        ...strategyInfo,
        gripDomainCategoryId: this.selectedGripDomainCategoryId,
      };
    }
    this.setInitialFormState();
  }
}
</script>
<style lang="scss" module>
.descriptionWordCount {
  color: $grey;
  font-size: $size-5;
  font-weight: $weight-regular;
  flex-direction: column-reverse; /* This specific flex property remains */
}

.linkInput {
  height: 44px;
  border-color: $grey-border-2 !important;
  color: $dark !important;
}

.deleteLinkColor {
  color: $secondary;
}

.sourceLink {
  color: $secondary;
  font-size: $size-6;
  font-weight: $weight-bold;
}
</style>
